import React, { useState, useEffect } from 'react';
import { ImageOff } from 'lucide-react';
import StepHeading from './StepHeading';

interface BuildCommercialStep8Props {
  finalCommercial: string;
  userName: string;
  selectedProduct: any;
  videoUrl: string;
  sessionId: string;
  publishData: any;
  isPublishing: boolean;
}

const toHandle = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const BuildCommercialStep8: React.FC<BuildCommercialStep8Props> = ({
  userName,
  selectedProduct,
  videoUrl,
  publishData,
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    const target = publishData?.azure_video_url || videoUrl;
    if (target) {
      setQrCodeUrl(
        `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(target)}`
      );
    }
  }, [publishData, videoUrl]);

  const isPublished = Boolean(publishData?.success);
  const productTitle = selectedProduct?.title || 'Your product';
  const productLink =
    publishData?.shopify_product_url ||
    (selectedProduct
      ? `https://superpossible.myshopify.com/products/${
          selectedProduct.handle || toHandle(selectedProduct.title)
        }`
      : '');

  return (
    <div className="ed-enter">
      <StepHeading
        index={8}
        kicker={isPublished ? 'In distribution' : 'Publishing'}
        title={isPublished ? productTitle : 'Sending it to the storefront…'}
        accent={isPublished ? 'has a commercial.' : undefined}
        lede={
          isPublished
            ? 'The film is live on the product page and ready to share.'
            : 'Hold tight while we upload the cut and attach it to the product.'
        }
      />

      <div className="ed-final ed-final--split">
        <div className="ed-final-media">
          {selectedProduct?.image_url ? (
            <img src={selectedProduct.image_url} alt={productTitle} />
          ) : (
            <div className="ed-final-placeholder" aria-hidden="true">
              <ImageOff />
            </div>
          )}
        </div>

        <div className="ed-final-body">
          <span className={`ed-badge${isPublished ? '' : ' ed-badge--pending'}`}>
            {isPublished ? 'Live on SuperPossible' : 'Publishing'}
          </span>

          <div className="ed-spec">
            <div className="ed-spec-row">
              <span className="ed-spec-key">Product</span>
              <span className="ed-spec-val">{productTitle}</span>
            </div>
            {selectedProduct?.price && (
              <div className="ed-spec-row">
                <span className="ed-spec-key">Price</span>
                <span className="ed-spec-val">${selectedProduct.price}</span>
              </div>
            )}
            <div className="ed-spec-row">
              <span className="ed-spec-key">Directed by</span>
              <span className="ed-spec-val">{userName || '—'}</span>
            </div>
            <div className="ed-spec-row">
              <span className="ed-spec-key">Status</span>
              <span className="ed-spec-val">
                {isPublished ? 'Live on SuperPossible' : 'Publishing…'}
              </span>
            </div>
          </div>

          {qrCodeUrl && (
            <div className="ed-qr" style={{ alignItems: 'flex-start' }}>
              <div className="ed-qr-frame">
                <img src={qrCodeUrl} alt="QR code linking to the commercial" />
              </div>
              <span className="ed-meta">Scan to watch it on your phone</span>
            </div>
          )}

          <div className="ed-actions">
            {productLink && (
              <a
                className="ed-btn ed-btn--primary ed-btn--lg"
                href={isPublished ? productLink : undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!isPublished}
                onClick={(e) => {
                  if (!isPublished) e.preventDefault();
                }}
              >
                {isPublished ? 'View product page' : 'Publishing…'}
              </a>
            )}
            <button
              type="button"
              className="ed-btn ed-btn--lg"
              onClick={() => window.location.reload()}
            >
              Make another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildCommercialStep8;
